import { gql } from "@apollo/client";
import { faPen, faPeopleGroup, faPerson, faRefresh, faSackDollar, faTrash } from "@fortawesome/free-solid-svg-icons";
import { ComponentStateTypes } from "components/common/ComponentState";
import HTMLCode from "components/common/HTMLCode";
import Page, { PageActionsType } from "components/common/page/Page";
import ItemView, { ItemViewPropsType } from "components/common/views/ItemView";
import { graph } from "config/axios-context";
import { AppRoutes, resolveRoute } from "config/router";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { MaintenanceAgricultureWorkDeleteDocument, MaintenanceAgricultureWorkViewPageQuery, MaintenanceAgricultureWorkViewPageQueryVariables } from "types/graphql";
import { Roles } from "utilities/app-utilities";
import { formatMoney } from "utilities/datalist/datalist-sort-utilities";
import { DateFormats, DateUtil } from "utilities/date-utilities";
import useAppContext from "utilities/hooks/useAppContext";
import * as lodash from "lodash";
import DeleteConfirmationModal from "components/common/confirmation/DeleteConfirmationModal";

const DELETE_MODAL_HASH = "maintenance-agriculture-work-view-delete-modal";

const query = gql`query MaintenanceAgricultureWorkViewPage($id: Int!) {
	maintenanceAgricultureWork(id: $id) {
		id
		note
			workers {
				items {
					person {
						name
						id
					}
					date
					price
					id
				}
			}
			season {
				name
				id
			}
			startDate
			endDate
			name
			logs {
				items {
					type {
						name
						id
					}
					investment {
						name
						id
						total
						investmentType
						startDate
					}
					id
					maintenanceLogFruitGroups {
						items {
							id
							fruitGroup {
								name
								id
							}
						}
					}
				}
			}

	}
	}
`;

const AgricultureMaintenanceViewPage = () => {
	const context = useAppContext();
	const params = useParams<{id: string}>();
	const [componentState, setComponentState] = useState<ComponentStateTypes>('loading');
	const [model, setModel] = useState<MaintenanceAgricultureWorkViewPageQuery["maintenanceAgricultureWork"]>(null!);

	useEffect(() => {
		if(!Number(params.id)) setComponentState('nodata');
		else loadData().then();
	 
	}, []);

	const pageActions: PageActionsType = [
		{
			icon: faRefresh,
			link: () => {
				setComponentState('loading');
				loadData().then();
			},
			title: context.t('button.reload'),
			name: "reload"
		},
		{
			icon: faPen,
			link: resolveRoute(AppRoutes.AGRICULTURE_MAINTENANCE_EDIT, {id: params.id}),
			hide: !context.userHasRole(Roles.AGRICULTURE_WORK_WRITE),
			title: context.t('button.edit'),
			name: "edit"
		},
		{
			icon: faTrash,
			link: () => context.modal.open(DELETE_MODAL_HASH),
			className: "text-danger",
			hide: !context.userHasRole(Roles.AGRICULTURE_WORK_WRITE),
			title: context.t('button.delete'),
			name: "delete"
		}
	]

	const loadData = async () => {
		try {
			const response = await graph<MaintenanceAgricultureWorkViewPageQuery, MaintenanceAgricultureWorkViewPageQueryVariables>({
				query,
				variables: {
					id: parseInt(params.id!)
				}
			});

			if(response.errors) {
				throw response.errors;
			}

			if(!response.data?.maintenanceAgricultureWork) {
				setComponentState('nodata');
			} else {
				setModel(response.data.maintenanceAgricultureWork);
				setComponentState('');
			}

		} catch (error: any) {
			context.notify('error', 'error.loading-data', error.message);
			setComponentState('error');
			throw error;
		}
	};

	const getData = (): ItemViewPropsType['model'] => {
		if(!model) return [];
		const startDate = new Date(model.startDate);
		const endDate = model.endDate ? new Date(model.endDate) : undefined;

        let logs: ItemViewPropsType['model'][0]['items'] = [];

        model.logs.items.forEach((l, i) => {
            logs = [
                ...logs,
				{
					label: '',
					value: i > 0 ? <div className="border-b border-dashed theme-border" /> : <></>,
					name: 'br-' + i
				},
                {
                    label: 'fields.type.shortName',
                    value: l.type.name,
                    name: 'type-' + i
                },
                {
                    label: 'fields.investment.shortName',
                    value: l.investment ? (<>
                        <span>{l.investment.name}</span>
                        <HTMLCode />
                        <span>({l.investment.total} <HTMLCode code="euro" />)</span>
                        <HTMLCode />
                        -
                        <HTMLCode />
                        <i>{context.t('agriculture.work.investmentTypes.' + l.investment.investmentType.toLowerCase())}</i>
                    </>) : '-',
                    name: 'investment-' + i
                },
                {
                    label: 'fields.group.shortName',
                    value: l.maintenanceLogFruitGroups.items.map(g => g.fruitGroup.name).join(', '),
                    name: 'group-' + i
                }
            ]
        })

		const data: ItemViewPropsType['model'] = [
			{
				items: [
					{
						label: 'fields.id.shortName',
						value: model.id,
						hide: 'compact',
						name: "id"
					},
					{
						label: 'fields.name.shortName',
						value: model.name,
						name: "name"
					},
					{
						label: 'fields.date.shortName',
						value: <>
							<span>{DateUtil.format(startDate, DateFormats.SHORT)}</span>
							{endDate && <span> - {DateUtil.format(endDate, DateFormats.SHORT)}</span>}
						</>,
						name: "date"
					},
				],
				name: 'id',
			},
			{
				name: 'logs',
				items: logs
			},
			{
				name: 'workers-data',
				items: [
					{
						icon: faPeopleGroup,
						label: 'fields.workersCount.name',
						name: 'workersCount',
						value: model.workers.items.length
					},
					{
						icon: faSackDollar,
						label: 'fields.workersSum.name',
						name: 'workersSum',
						value: <>{formatMoney(lodash.sumBy(model.workers.items, i => i.price))} <HTMLCode code="euro" /></>
					},
					{
						icon: faPeopleGroup,
						label: 'fields.workers.shortName',
						name: 'workers',
						hide: 'compact',
						value: ' '
					},
					...(model.workers.items.map((w, i) => {
						return {
							icon: faPerson,
							label: '',
							name: 'person-' + i,
							hide: 'compact',
							value: <>{i + 1}. {w?.person?.name}, <i>{formatMoney(w?.price)} <HTMLCode code="euro" /></i></>
						}
					})) as any,
				],
				className: {
					compact: 'grid text-center grid-cols-2'
				}
			},
			{
				name: 'notes',
				items: [
					{
						label: 'fields.note.name',
						name: 'note',
						value: model.note,
						hide: "compact",
						propertyType: 'note'
					}
				]
			}
		];

		return data;
	}

	return (
		<>
			<Page title="pages.agriculture.work.maintenance.view" state={componentState} actions={pageActions}>
				<ItemView name="agricultureMaintenanceWorkViewPage" model={getData()} view='default' />
			</Page>
			<DeleteConfirmationModal name={DELETE_MODAL_HASH} onConfirm={MaintenanceAgricultureWorkDeleteDocument} id={Number(params.id)} />
		</>
	);
};

export default AgricultureMaintenanceViewPage;
